import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Trophy, Medal, Award, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, Spinner, Field, StatusChip } from "@/components/admin/ui";
import { Calendar } from "@/components/ui/calendar";

const STATUSES = ["upcoming", "active", "completed", "cancelled"];
const toLocalInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const off = dt.getTimezoneOffset();
  return new Date(dt.getTime() - off * 60000).toISOString().slice(0, 16);
};
const toTimeInput = (d) => {
  if (!d) return "00:00";
  const dt = new Date(d);
  return `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
};
const dayKey = (d) => format(d, "yyyy-MM-dd");
const combineDayTime = (d, hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const dt = new Date(d);
  dt.setHours(h || 0, m || 0, 0, 0);
  return dt;
};

const PLACES = [
  { pos: 1, label: "1st Place", icon: Trophy, color: "#ffd23f" },
  { pos: 2, label: "2nd Place", icon: Medal, color: "#cbd5e1" },
  { pos: 3, label: "3rd Place", icon: Award, color: "#d98a4b" },
];

export default function AdminTournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [stations, setStations] = useState([]);
  const [earnRules, setEarnRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [picks, setPicks] = useState({ 1: "", 2: "", 3: "" });
  const [finalizing, setFinalizing] = useState(false);
  const [results, setResults] = useState(null);
  const [emailOnFinalize, setEmailOnFinalize] = useState(false);

  const loadResults = () => {
    apiClient.get(`/admin/tournaments/${id}/results`).then(({ data }) => setResults(data)).catch(() => {});
  };
  const overrideResult = async (userId, dayIndex, position) => {
    try {
      await apiClient.put(`/admin/tournaments/${id}/results`, { user_id: userId, day_index: dayIndex, position: position === "" ? null : Number(position) });
      loadResults();
    } catch (err) { toast.error(getErrorMessage(err, "Override failed")); }
  };
  const prefillPodium = () => {
    const top = (results?.standings || []).slice(0, 3);
    setPicks({ 1: top[0]?.user_id || "", 2: top[1]?.user_id || "", 3: top[2]?.user_id || "" });
    toast.success("Podium pre-filled from standings");
  };

  const load = () => {
    Promise.all([
      apiClient.get(`/admin/tournaments/${id}/detail`),
      apiClient.get("/stations"),
      apiClient.get("/admin/earn-rules"),
    ])
      .then(([d, s, r]) => {
        setDetail(d.data);
        setStations(s.data);
        setEarnRules(r.data);
        // Days come as [{start_datetime, end_datetime}]; fall back to single day using top-level start/end
        const rawDays = (d.data.days && d.data.days.length > 0)
          ? d.data.days
          : (d.data.start_datetime && d.data.end_datetime
            ? [{ start_datetime: d.data.start_datetime, end_datetime: d.data.end_datetime }]
            : []);
        const dayDates = rawDays.map((rd) => new Date(rd.start_datetime)).sort((a, b) => a - b);
        const firstDay = rawDays[0];
        setForm({
          name: d.data.name, description: d.data.description, game_type: d.data.game_type,
          selected_stations: d.data.selected_stations || [],
          max_participants: d.data.max_participants, entry_fee: d.data.entry_fee, prize_pool: d.data.prize_pool,
          status: d.data.status, rules: d.data.rules || "",
          days: dayDates,
          daily_start_time: firstDay ? toTimeInput(firstDay.start_datetime) : "18:00",
          daily_end_time: firstDay ? toTimeInput(firstDay.end_datetime) : "21:00",
          registration_deadline: toLocalInput(d.data.registration_deadline),
        });
        const w = d.data.winners || [];
        setPicks({
          1: w.find((x) => x.position === 1)?.user_id || "",
          2: w.find((x) => x.position === 2)?.user_id || "",
          3: w.find((x) => x.position === 3)?.user_id || "",
        });
      })
      .catch((err) => toast.error(getErrorMessage(err, "Failed to load tournament")))
      .finally(() => setLoading(false));
    loadResults();
  };
  useEffect(load, [id]);

  const toggleStation = (sid) => setForm((f) => ({
    ...f, selected_stations: f.selected_stations.includes(sid) ? f.selected_stations.filter((x) => x !== sid) : [...f.selected_stations, sid],
  }));

  const removeDay = (d) => setForm((f) => ({ ...f, days: f.days.filter((x) => dayKey(x) !== dayKey(d)) }));

  const save = async () => {
    if (form.days.length === 0) { toast.error("Select at least one competition day"); return; }
    const [sh, sm] = form.daily_start_time.split(":").map(Number);
    const [eh, em] = form.daily_end_time.split(":").map(Number);
    if (eh * 60 + em <= sh * 60 + sm) { toast.error("Daily end time must be after start time"); return; }
    const days = form.days.map((d) => ({
      start_datetime: combineDayTime(d, form.daily_start_time).toISOString(),
      end_datetime: combineDayTime(d, form.daily_end_time).toISOString(),
    }));
    setSaving(true);
    try {
      await apiClient.put(`/admin/tournaments/${id}`, {
        name: form.name, description: form.description, game_type: form.game_type,
        selected_stations: form.selected_stations, max_participants: Number(form.max_participants),
        entry_fee: Number(form.entry_fee), prize_pool: Number(form.prize_pool),
        status: form.status, rules: form.rules,
        days,
        registration_deadline: new Date(form.registration_deadline).toISOString(),
      });
      toast.success("Tournament updated");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); } finally { setSaving(false); }
  };

  const finalizeWinners = async () => {
    const winners = PLACES.filter((p) => picks[p.pos]).map((p) => ({ user_id: picks[p.pos], position: p.pos }));
    if (winners.length === 0) { toast.error("Select at least one winner"); return; }
    const ids = winners.map((w) => w.user_id);
    if (new Set(ids).size !== ids.length) { toast.error("A user cannot hold more than one position"); return; }
    setFinalizing(true);
    try {
      const { data } = await apiClient.post(`/admin/tournaments/${id}/winners`, { winners, send_email: emailOnFinalize });
      toast.success(data.message || "Winners assigned");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to assign winners")); } finally { setFinalizing(false); }
  };

  if (loading || !form) return <Spinner label="Loading tournament" />;

  const finalized = detail.results_finalized;
  const participants = detail.participants || [];
  const nameOf = (uid) => participants.find((p) => p.id === uid)?.name || uid;

  return (
    <div data-testid="tournament-detail-page">
      <button onClick={() => navigate("/admin/tournaments")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-4" data-testid="tournament-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to Tournaments
      </button>
      <AdminHeader title={detail.name} subtitle={<span className="inline-flex items-center gap-3">{detail.game_type} · <StatusChip status={detail.status} /></span>} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Edit form */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-bold text-white mb-4">Edit Details</h3>
          <Field label="Name"><input className="input-cyber" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="td-name" /></Field>
          <Field label="Game Type"><input className="input-cyber" value={form.game_type} onChange={(e) => setForm({ ...form, game_type: e.target.value })} data-testid="td-game" /></Field>
          <Field label="Description"><textarea className="input-cyber min-h-[60px] resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="td-desc" /></Field>
          <Field label="Stations">
            <div className="flex flex-wrap gap-2">
              {stations.map((s) => {
                const sel = form.selected_stations.includes(s.id);
                return <button key={s.id} type="button" onClick={() => toggleStation(s.id)} className="chip" data-testid={`td-station-${s.id}`}
                  style={{ background: sel ? "rgba(0,229,255,0.15)" : "rgba(8,11,22,0.6)", border: `1px solid ${sel ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.2)"}`, color: sel ? "#00e5ff" : "#94a3b8" }}>
                  {s.name}
                </button>;
              })}
            </div>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Max Players"><input type="number" min="2" className="input-cyber" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} data-testid="td-max" /></Field>
            <Field label="Entry ($)"><input type="number" min="0" className="input-cyber" value={form.entry_fee} onChange={(e) => setForm({ ...form, entry_fee: e.target.value })} data-testid="td-fee" /></Field>
            <Field label="Prize ($)"><input type="number" min="0" className="input-cyber" value={form.prize_pool} onChange={(e) => setForm({ ...form, prize_pool: e.target.value })} data-testid="td-prize" /></Field>
          </div>
          <Field label="Status">
            <select className="input-cyber capitalize" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="td-status">
              {STATUSES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
            </select>
          </Field>
          <Field label="Competition Days">
            <div className="space-y-2">
              <div className="rounded-xl glass border border-white/10 p-2 inline-block" data-testid="td-calendar">
                <Calendar mode="multiple" selected={form.days}
                  onSelect={(dates) => setForm((f) => ({ ...f, days: (dates || []).slice().sort((a, b) => a - b) }))}
                  initialFocus />
              </div>
              {form.days.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="td-day-chips">
                  {form.days.map((d) => (
                    <span key={dayKey(d)} className="chip inline-flex items-center gap-1.5" data-testid={`td-day-${dayKey(d)}`}
                      style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.45)", color: "#00e5ff" }}>
                      {format(d, "EEE, MMM d")}
                      <button type="button" onClick={() => removeDay(d)} className="hover:text-white" data-testid={`td-day-remove-${dayKey(d)}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-slate-500 text-xs">Click a date on the calendar to add it; click again to remove.</p>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Daily Start Time"><input type="time" className="input-cyber" value={form.daily_start_time} onChange={(e) => setForm({ ...form, daily_start_time: e.target.value })} data-testid="td-daily-start" /></Field>
            <Field label="Daily End Time"><input type="time" className="input-cyber" value={form.daily_end_time} onChange={(e) => setForm({ ...form, daily_end_time: e.target.value })} data-testid="td-daily-end" /></Field>
          </div>
          <p className="text-slate-500 text-xs -mt-2 mb-3">These times apply to every competition day.</p>
          <Field label="Registration Deadline"><input type="datetime-local" className="input-cyber" value={form.registration_deadline} onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} data-testid="td-deadline" /></Field>
          <Field label="Rules"><textarea className="input-cyber min-h-[60px] resize-none" value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} data-testid="td-rules" /></Field>
          <button onClick={save} disabled={saving} className="neon-btn w-full py-3" data-testid="td-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>

        {/* Winners */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-bold text-white mb-1">Podium & Winners</h3>
          <p className="text-slate-400 text-sm mb-4">{participants.length} registered participant{participants.length === 1 ? "" : "s"}. Placement points: 1st {earnRules?.tournament_first?.toLocaleString()} · 2nd {earnRules?.tournament_second?.toLocaleString()} · 3rd {earnRules?.tournament_third?.toLocaleString()}.</p>

          {finalized ? (
            <div className="space-y-3" data-testid="winners-finalized">
              {(detail.winners || []).map((w) => {
                const place = PLACES.find((p) => p.pos === w.position);
                const Icon = place?.icon || Trophy;
                return (
                  <div key={w.position} className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: `${place?.color}12`, border: `1px solid ${place?.color}44` }}>
                    <span className="flex items-center gap-3 text-white font-semibold"><Icon className="w-5 h-5" style={{ color: place?.color }} />{place?.label} — {nameOf(w.user_id)}</span>
                    <span style={{ color: "#00ff9d" }} className="font-bold">+{w.points?.toLocaleString()} pts</span>
                  </div>
                );
              })}
              <p className="text-slate-500 text-xs mt-2">Results are final. Points have been awarded and winners notified.</p>
            </div>
          ) : participants.length === 0 ? (
            <p className="text-slate-500 py-8 text-center" data-testid="winners-no-participants">No participants have registered yet. Winners can be assigned once players register.</p>
          ) : (
            <div className="space-y-4">
              {PLACES.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.pos}>
                    <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2" style={{ color: p.color }}><Icon className="w-4 h-4" /> {p.label}</label>
                    <select className="input-cyber" value={picks[p.pos]} onChange={(e) => setPicks({ ...picks, [p.pos]: e.target.value })} data-testid={`winner-pick-${p.pos}`}>
                      <option value="" className="bg-slate-900">— None —</option>
                      {participants.map((u) => <option key={u.id} value={u.id} className="bg-slate-900">{u.name}</option>)}
                    </select>
                  </div>
                );
              })}
              <button onClick={finalizeWinners} disabled={finalizing} className="neon-btn w-full py-3 mt-2" data-testid="assign-winners-btn">
                {finalizing ? <><Loader2 className="w-4 h-4 animate-spin" /> Assigning...</> : <><Trophy className="w-4 h-4" /> Assign Winners & Award Points</>}
              </button>
              <label className="flex items-center gap-2 text-sm text-slate-300 mt-2 cursor-pointer" data-testid="finalize-email-toggle">
                <input type="checkbox" checked={emailOnFinalize} onChange={(e) => setEmailOnFinalize(e.target.checked)} className="accent-cyan-400" />
                Email final results to all participants
              </label>
              <p className="text-slate-500 text-xs">Assigning winners is final — it awards placement points and notifies each winner and all participants.</p>
            </div>
          )}
        </div>
      </div>

      {results && results.tournament_type !== "gaming" && (
        <div className="glass rounded-2xl p-6 mt-6" data-testid="tournament-standings-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-white">Auto Standings <span className="text-xs text-slate-500 uppercase">({results.tournament_type === "time_attack" ? "Time Attack" : "Racing"})</span></h3>
              <p className="text-slate-400 text-sm">Overall points across {results.days} day{results.days === 1 ? "" : "s"}. Edit a day&apos;s position to apply a manual override (e.g. penalty).</p>
            </div>
            {!finalized && (results.standings || []).length > 0 && (
              <button onClick={prefillPodium} className="text-cyan-400 text-sm font-semibold hover:text-cyan-300" data-testid="prefill-podium-btn">Use for podium →</button>
            )}
          </div>
          {(results.standings || []).length === 0 ? (
            <p className="text-slate-500 py-6 text-center" data-testid="standings-empty">No results logged yet. Results appear automatically when registered players check in during tournament hours.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="standings-table">
                <thead>
                  <tr className="text-slate-400 text-left border-b border-white/10">
                    <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Driver</th>
                    {Array.from({ length: results.days }).map((_, di) => <th key={di} className="py-2 px-2 text-center">Day {di + 1}</th>)}
                    <th className="py-2 pl-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {results.standings.map((r) => (
                    <tr key={r.user_id} className="border-b border-white/5" data-testid={`standings-row-${r.user_id}`}>
                      <td className="py-2 pr-3 font-bold" style={{ color: r.rank <= 3 ? "#ffd23f" : "#94a3b8" }}>P{r.rank}</td>
                      <td className="py-2 pr-3 text-white">{r.name}</td>
                      {Array.from({ length: results.days }).map((_, di) => {
                        const d = r.days?.[String(di)];
                        return (
                          <td key={di} className="py-1.5 px-2 text-center">
                            <input type="number" min="1" defaultValue={d?.position ?? ""} disabled={finalized}
                              onBlur={(e) => { if (String(e.target.value) !== String(d?.position ?? "")) overrideResult(r.user_id, di, e.target.value); }}
                              className="input-cyber w-14 text-center px-1 py-1" data-testid={`override-${r.user_id}-${di}`}
                              title={d?.manual ? "Manual override" : "Auto"} style={d?.manual ? { borderColor: "#ffb020" } : {}} />
                            <div className="text-[10px] text-slate-500">{d?.points ?? 0} pts</div>
                          </td>
                        );
                      })}
                      <td className="py-2 pl-3 text-right font-bold" style={{ color: "#00ff9d" }}>{r.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
